import * as registeredSearchPlugins from 'search/plugins/registeredSearchPlugins';
import {SearchPlugin, SearchPluginColumnPosition} from 'search/plugins/SearchPlugin';
import {ListableItem} from 'search/SearchConstants';
import sortedBy from 'util/sortedBy';
import {BasicEvent} from 'util/Types';

import {
  ColumnConfiguration,
  ListingRenderer,
  ListingRendererType,
  ListingTypes,
  getModelTypesForListingRendererType,
} from './ListingRendererConstants';

export default abstract class BaseListingRendererWithPlugins<
  T extends ListingRendererType,
> implements ListingRenderer<T> {
  // This is required to allow us to call getModelTypesForListingRendererType.
  abstract listingRendererType: ListingRendererType;

  // These two methods will be folded into the getColumnConfigurations and getRowContentHeight results,
  // respectively, along side the plugin content.
  abstract getNonPluginColumnConfigurations(
    items: Array<ListingTypes[T]>,
    extras: unknown
  ): Array<ColumnConfiguration<ListingTypes[T]>>;
  abstract getNonPluginRowContentHeight(item: ListingTypes[T], extras: unknown): number;

  getColumnConfigurations(
    items: Array<ListingTypes[T]>,
    extras: unknown = {}
  ): Array<ColumnConfiguration<ListingTypes[T]>> {
    const columnKeys = new Set<string>();
    const columnConfigurationsByPosition = new Map<
      SearchPluginColumnPosition,
      Array<ColumnConfiguration<ListableItem>>
    >();

    const columnConfigurations = this.getNonPluginColumnConfigurations(items, extras);
    columnConfigurationsByPosition.set(
      SearchPluginColumnPosition.LISTING_RENDERER,
      columnConfigurations as Array<ColumnConfiguration<ListableItem>>
    );
    columnConfigurations.forEach((columnConfig) => columnKeys.add(columnConfig.column.accessorKey));

    for (const plugin of this.getApplicablePlugins()) {
      if (!columnConfigurationsByPosition.has(plugin.columnPosition)) {
        columnConfigurationsByPosition.set(plugin.columnPosition, []);
      }

      const pluginPositionColumnConfigs = columnConfigurationsByPosition.get(plugin.columnPosition)!;
      plugin.getColumnConfigurations(items as Array<ListableItem>, extras).forEach((pluginColumnConfig) => {
        if (!columnKeys.has(pluginColumnConfig.column.accessorKey)) {
          pluginPositionColumnConfigs.push(pluginColumnConfig);
          columnKeys.add(pluginColumnConfig.column.accessorKey);
        }
      });
    }

    return sortedBy(Array.from(columnConfigurationsByPosition.keys()), [
      (columnPosition: SearchPluginColumnPosition) => columnPosition,
    ]).flatMap(
      (position: SearchPluginColumnPosition) => columnConfigurationsByPosition.get(position)!
    ) as Array<ColumnConfiguration<ListingTypes[T]>>;
  }

  getRowContentHeight(item: ListingTypes[T], extras: unknown = {}): number {
    return Math.max(
      this.getNonPluginRowContentHeight(item, extras),
      ...this.getApplicablePlugins().map((plugin) => plugin.getRowContentHeight(item as ListableItem, extras))
    );
  }

  abstract renderIcon(item: ListingTypes[T], options: unknown): JSX.Element;

  abstract openContextMenu(item: ListingTypes[T], e: BasicEvent, options: unknown): void;

  getApplicablePlugins(): Array<SearchPlugin> {
    const modelTypes = getModelTypesForListingRendererType(this.listingRendererType);
    return registeredSearchPlugins
      .getRegisteredSearchPlugins()
      .filter((plugin) => modelTypes.some((modelType) => plugin.pluginApplies(modelType)));
  }
}
